import { useState, useEffect, useCallback } from 'react';
import { FileText, Video, Music, File, Loader2 } from 'lucide-react';
import { attachmentsApi, Attachment } from '@/lib/api';
import { formatBytes } from '@/lib/utils';
import { FilePreviewModal } from './FilePreviewModal';
import { AttachmentThumbnail } from './AttachmentThumbnail';

interface AttachmentDisplayProps {
  attachments: Attachment[];
  className?: string;
}

// Get icon for file type
function getFileIcon(mimeType: string) {
  if (mimeType.startsWith('video/')) return Video;
  if (mimeType.startsWith('audio/')) return Music;
  if (mimeType === 'application/pdf') return FileText;
  return File;
}

// Thumbnail tile that fetches presigned URLs, then lets AttachmentThumbnail
// handle the just-uploaded race and the fallbacks.
function ImageThumbnail({
  attachment,
  onClick
}: {
  attachment: Attachment;
  onClick: () => void;
}) {
  const [urls, setUrls] = useState<{ downloadUrl: string; thumbnailSmUrl?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchUrls = useCallback(async () => {
    const result = await attachmentsApi.getDownloadUrl(attachment.id);
    setUrls(result);
  }, [attachment.id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    attachmentsApi
      .getDownloadUrl(attachment.id)
      .then((result) => {
        if (!cancelled) setUrls(result);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [attachment.id]);

  return (
    <button
      onClick={onClick}
      className="relative group overflow-hidden rounded-lg border border-grey-200 hover:border-accent transition-colors bg-grey-100"
      title={attachment.fileName}
    >
      <div className="w-24 h-24 flex items-center justify-center">
        {loading ? (
          <Loader2 className="w-6 h-6 text-grey-400 animate-spin" />
        ) : error || !urls ? (
          <File className="w-8 h-8 text-grey-400" />
        ) : (
          <AttachmentThumbnail
            thumbnailUrl={urls.thumbnailSmUrl}
            originalUrl={urls.downloadUrl}
            mimeType={attachment.mimeType}
            fileName={attachment.fileName}
            createdAt={attachment.createdAt}
            onRefresh={fetchUrls}
            className="w-full h-full"
            imgClassName="w-full h-full object-cover"
          />
        )}
      </div>

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-secondary/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <span className="text-white text-xs font-medium">View</span>
      </div>

      {/* Filename overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-secondary/80 to-transparent p-1">
        <p className="text-[10px] text-white truncate">{attachment.fileName}</p>
      </div>
    </button>
  );
}

// File chip component for non-image files
function FileChip({ 
  attachment, 
  onClick 
}: { 
  attachment: Attachment; 
  onClick: () => void;
}) {
  const FileIcon = getFileIcon(attachment.mimeType);

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 bg-grey-50 hover:bg-grey-100 rounded-lg border border-grey-200 text-sm transition-colors"
      title="Click to preview"
    >
      <FileIcon className="h-4 w-4 text-grey-500" />
      <span className="max-w-[150px] truncate text-secondary">
        {attachment.fileName}
      </span>
      <span className="text-grey-400 text-xs">
        {formatBytes(attachment.fileSize)}
      </span>
    </button>
  );
}

export function AttachmentDisplay({ attachments, className = '' }: AttachmentDisplayProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  if (!attachments || attachments.length === 0) return null;

  const selectedAttachment = selectedIndex !== null ? attachments[selectedIndex] : null;

  return (
    <>
      <div className={`${className}`}>
        <div className="flex flex-wrap gap-2">
          {attachments.map((attachment, i) => (
            <ImageThumbnail
              key={attachment.id}
              attachment={attachment}
              onClick={() => setSelectedIndex(i)}
            />
          ))}
        </div>
      </div>

      {/* Preview Modal */}
      <FilePreviewModal
        isOpen={selectedIndex !== null}
        onClose={() => setSelectedIndex(null)}
        attachment={selectedAttachment}
        attachments={attachments}
        currentIndex={selectedIndex ?? 0}
        onNavigate={(index) => setSelectedIndex(index)}
      />
    </>
  );
}
